// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
/* Starter Custom Code
import {
    HasProductContext,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
*/
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
// import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select"; // Store Custom Code
import * as React from "react";
import { connect } from "react-redux";

/* Starter Custom Code - Start */
/* Store Custom Code
import {
    HasProductContext,
    ProductContext,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
*/
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import { useContext } from "react";
import { css } from "styled-components";

/* Store Custom Code - Start */
import {
    HasProductContext,
    ProductContext,
    ProductContextModel,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import constants from "@store/constants";
import useCanOnlyOrderSample from "@store/Framework/Common/Hooks/samples/useCanOnlyOrderSample";
import formatUoMTranslation from "@store/Framework/Common/Utilities/formatUomTranslation";
import Select, { SelectPresentationProps, SelectProps } from "@store/MobiusOverrides/Select/Select";
import { useCanAddToCart } from "@toolkit/Framework/Store/Data/Products/ProductsHooks";

const showSingleUOM = constants.showSingleUOM;
/* Store Custom Code - End */

export interface ProductUnitOfMeasureSelectProps extends Omit<OwnProps, keyof SelectPresentationProps> {}

export const useShowProductUnitOfMeasureSelect = (
    productContextOverride?: ProductContextModel, // Store Custom Code
) => {
    const { productSettings } = useSettingsCollection();
    /* Store Custom Code - Start */
    const hookProductContext = useContext(ProductContext);
    const { product, productInfo } = productContextOverride || hookProductContext;
    const canAddToCart = useCanAddToCart(product, productInfo);
    const canOnlyOrderSample = useCanOnlyOrderSample(product);

    return !(
        !productSettings?.alternateUnitsOfMeasure ||
        !product.unitOfMeasures ||
        product.unitOfMeasures.filter(uom => uom.unitOfMeasure).length <= (showSingleUOM ? 0 : 1) ||
        !canAddToCart ||
        (canOnlyOrderSample && !showSingleUOM)
    );
    /* Store Custom Code - End */
};
/* Starter Custom Code - End */

interface OwnProps extends SelectProps {
    labelOverride?: React.ReactNode;
    disabled?: boolean;
    extendedStyles?: SelectPresentationProps;
    // Starter Custom Code
    overridedStyles?: SelectPresentationProps;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

export const productUnitOfMeasureSelectStyles: SelectPresentationProps = {
    sizeVariant: "small",
    /* Store Custom Code - Start */
    mergeCss: true,
    /* Store Custom Code - End */
    cssOverrides: {
        /* Store Removed - x.theme.formField.defaultProps?.cssOverrides?.descriptionWrapper */
        /* Store Removed - x.theme.formField.defaultProps?.cssOverrides?.formField */
        /* Store Removed - x.theme.formField.defaultProps?.cssOverrides?.formInputWrapper */
        /* Store Removed - x.theme.formField.defaultProps?.cssOverrides?.inputSelect */
    },
};

const ProductUnitOfMeasureSelect: React.FC<Props> = ({
    productContext, // Store Custom Code
    disabled: baseDisabled, // Store Custom Code
    productSettings,
    labelOverride,
    extendedStyles,
    overridedStyles, // Starter Custom Code
    ...otherProps
}) => {
    const styles = useExpandProps(productUnitOfMeasureSelectStyles, extendedStyles, overridedStyles); // Starter Custom Code

    /* Store Custom Code - Start */
    const {
        product: { unitOfMeasures },
        productInfo: { unitOfMeasure, productId },
        onUnitOfMeasureChanged,
    } = productContext;

    const canOnlyOrderSample = useCanOnlyOrderSample(productContext.product);
    /* Store Custom Code - End */

    if (!useShowProductUnitOfMeasureSelect() /* Store Custom Code */) {
        return null;
    }

    const uomChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (!onUnitOfMeasureChanged) {
            logger.warn(`There was no onUnitOfMeasureChanged passed to the ProductContext for ${productId}`);
            return;
        }
        onUnitOfMeasureChanged(event.target.value);
    };

    /* Store Custom Code - Start */
    const baseUoM = unitOfMeasures?.[0] ?? null;

    const disabled =
        baseDisabled ||
        canOnlyOrderSample ||
        Boolean(unitOfMeasures && unitOfMeasures.filter(uom => uom.unitOfMeasure).length <= 1);
    /* Store Custom Code - End */

    return (
        <Select
            label={labelOverride ?? translate("U/M")}
            value={unitOfMeasure}
            onChange={uomChangeHandler}
            disabled={disabled}
            data-test-selector="product_unitOfMeasureSelect"
            {...styles}
            {...otherProps}
        >
            {canOnlyOrderSample /* Store Custom Code */ ? (
                <option value={constants.sampleUOM}>{translate(`${constants.sampleUOM}`)}</option>
            ) : (
                <>
                    {/* Starter Custom Code - next line - "?" */}
                    {unitOfMeasures?.map(uom => (
                        <option key={uom.id.toString()} value={uom.unitOfMeasure}>
                            {/* Store Custom Code - Start */}
                            {formatUoMTranslation(uom.unitOfMeasure, 1)}{" "}
                            {uom.qtyPerBaseUnitOfMeasure !== 1 && (
                                <>
                                    ({uom.qtyPerBaseUnitOfMeasure}{" "}
                                    {formatUoMTranslation(baseUoM!.unitOfMeasure, uom.qtyPerBaseUnitOfMeasure)})
                                </>
                            )}
                            {/* Store Custom Code - End */}
                        </option>
                    ))}
                </>
            )}
        </Select>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductUnitOfMeasureSelect));
